<template>
  <div id="main-wrapper">

    <NavbarDark/>
    <ExhibitHeader
      v-if="!pageLoading"
      :coverImage="selectedExhibit.coverImage"
      :mapImage="selectedExhibit.mapImage"
      :logo="selectedExhibit.logo"
      :name="selectedExhibit.name"
      :description="selectedExhibit.description"
      :start_date="selectedExhibit.start_date"
      :end_date="selectedExhibit.end_date"
      :halls_count="selectedExhibit.halls_count"
    />
    <section class="gray-simple">
      <div v-if="pageLoading" class="container py-5">
        <div class="row g-4">
          <div v-for="i in 1" :key="i" class="col-md-6">
            <div class="skeleton-card"></div>
          </div>
        </div>
      </div>

      <div v-else class="home-map-banner half-map">
        <div class="container">
          <div class="row mt-4">

            <div class="col-lg-6">
              <ExhibitBody
                v-if="selectedExhibit.name !== 'Loading...'"
                :exhibit="selectedExhibit"
                :border="true"
                :visitorCount="visitorCount"
              />
            </div>

            <div class="col-lg-6">

              <div class="sty_1523 mb-3">
                <h6>{{ $t('Available Halls') }}</h6>
              </div>

              <div
                v-for="(item,index) in hallsData"
                :key="index"
                class="py-2"
              >
                <ListHall :item="item" :border="true"/>
              </div>

              <div id="scroll-sentinel" class="text-center my-4">
                <span v-if="loading">
                  {{ $t('Downloading..') }}
                </span>
              </div>

            </div>

          </div>
        </div>
      </div>
    </section>
    <FooterTop :bg="'theme-bg'"/>

    <FooterDark/>

    <ScrollToTop/>
  </div>
</template>

<script setup>
import {ref, computed, watch, onMounted, onUnmounted, nextTick} from "vue"
import {useRoute, useRouter} from "vue-router"
import {useI18n} from "vue-i18n"
import axios from "axios"
import ScrollToTop from '@/components/scroll-to-top.vue';
import NavbarDark from "@/components/navbar/navbar-dark.vue"
import FooterDark from '@/components/footer/footer-dark.vue'
import FooterTop from '@/components/footer/footer-top.vue'
import ExhibitHeader from "@/components/ExhibitHeader.vue"
import ExhibitBody from "@/components/ExhibitBody.vue"
import ListHall from "@/components/property/list-hall"
import { useHead } from '@vueuse/head'
const router = useRouter()
const route = useRoute()
const {locale} = useI18n()

const selectedExhibit = ref({name: "Loading..."})
const hallsData = ref([])
const visitorCount = ref(0)

const currentPage = ref(1)
const totalPages = ref(1)

const loading = ref(false)
const pageLoading = ref(true)

const observer = ref(null)
const abortController = ref(null)

const exhibitSlug = computed(() => route.params.slug)

async function fetchExhibitDetails() {

  if (!exhibitSlug.value) return

  pageLoading.value = true

  abortController.value?.abort()
  abortController.value = new AbortController()

  try {

    const res = await axios.get(
        `/exhibits/show-by-slug/${exhibitSlug.value}`,
        {
          params: {lang: locale.value},
          signal: abortController.value.signal
        }
    )

    if (!res.data?.data?.id) {
      router.replace({name: "NotFound"})
      return
    }

    const exhibit = res.data.data

    selectedExhibit.value = {
      id: exhibit.id,
      slug: exhibit.slug || {},
      coverImage: exhibit.cover || "",
      logo: exhibit.logo || "",
      seo_image: exhibit.seo_image || "",
      mapImage: exhibit.design_image || "",
      name: exhibit.name || "",
      description: exhibit.description || "",
      location: exhibit.location || "",
      seo_title: exhibit.seo_title || "",
      seo_description: exhibit.seo_description || "",
      start_date: exhibit.start_date || "",
      end_date: exhibit.end_date || "",
      halls_count: exhibit.halls_count || 0,
      tags: exhibit.tags || [],
      ratings: exhibit.ratings || [],
      focus_keywords: exhibit.focus_keywords || []
    }

  } catch (e) {
    if (!axios.isCancel(e)) console.error(e)
  } finally {
    pageLoading.value = false
  }
}


async function fetchHalls() {

  if (!selectedExhibit.value.id) return
  if (loading.value) return

  loading.value = true

  try {

    const res = await axios.get(
        `/${selectedExhibit.value.id}/halls`,
        {
          params: {
            page: currentPage.value,
            lang: locale.value
          }
        }
    )

    const responseData = res.data

    const halls = responseData?.data || []
    
    if (halls.length) {
      hallsData.value.push(...halls)
      currentPage.value++
    }

    totalPages.value =
        responseData?.meta?.last_page ||
        responseData?.meta?.last_page ||
        1

  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

async function trackVisitor() {

  if (!selectedExhibit.value.id) return

  try {

    let visitorKey = localStorage.getItem("visitor_key")

    if (!visitorKey) {
      visitorKey = crypto.randomUUID()
      localStorage.setItem("visitor_key", visitorKey)
    }

    await axios.post(
        `/visitor/exhibit/${selectedExhibit.value.id}`,
        {},
        {headers: {"X-Visitor-Key": visitorKey}}
    )

    const res = await axios.get(
        `/visitor/exhibit/${selectedExhibit.value.id}`
    )

    visitorCount.value = res.data?.visitor_count || 0

  } catch (e) {
    console.error(e)
  }
}


async function setupObserver() {

  if (observer.value) observer.value.disconnect()

  await nextTick()

  const sentinel = document.getElementById("scroll-sentinel")
  if (!sentinel) return

  observer.value = new IntersectionObserver(
      async entries => {

        const entry = entries[0]

        if (
            entry.isIntersecting &&
            !loading.value &&
            currentPage.value <= totalPages.value
        ) {
          await fetchHalls()
        }

      },
      {threshold: 0.15}
  )

  observer.value.observe(sentinel)
}
///// SEO && Google
const baseUrl = 'https://ruqm.me'

const canonicalUrl = computed(() => {
  if (!selectedExhibit.value?.slug?.[locale.value]) return ''
  return `${baseUrl}/${locale.value}/exhibit/${selectedExhibit.value.slug[locale.value]}`
})

const hreflangLinks = computed(() => {
  if (!selectedExhibit.value?.slug) return []

  return Object.entries(selectedExhibit.value.slug).map(([lang, slug]) => ({
    rel: 'alternate',
    hreflang: lang,
    href: `${baseUrl}/${lang}/exhibit/${slug}`
  }))
})

const xDefault = computed(() => {
  const enSlug = selectedExhibit.value?.slug?.en
  if (!enSlug) return null

  return {
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${baseUrl}/en/exhibit/${enSlug}`
  }
})

useHead(() => {
  if (!selectedExhibit.value?.id) return {}

  return {
    title: selectedExhibit.value.seo_title || selectedExhibit.value.name,
    meta: [
      {
        name: 'description',
        content: selectedExhibit.value.seo_description || ''
      },
      {
        name: 'keywords',
        content: selectedExhibit.value.focus_keywords?.join(', ') || ''
      },

      { property: 'og:type', content: 'event' },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'og:title', content: selectedExhibit.value.seo_title },
      { property: 'og:description', content: selectedExhibit.value.seo_description },
      { property: 'og:image', content: selectedExhibit.value.seo_image },

      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: selectedExhibit.value.seo_title },
      { name: 'twitter:description', content: selectedExhibit.value.seo_description },
      { name: 'twitter:image', content: selectedExhibit.value.seo_image },
    ],
    link: [
      { rel: 'canonical', href: canonicalUrl.value },
      ...hreflangLinks.value,
      ...(xDefault.value ? [xDefault.value] : [])
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          "name": selectedExhibit.value.name,
          "startDate": selectedExhibit.value.start_date,
          "endDate": selectedExhibit.value.end_date,
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "eventStatus": "https://schema.org/EventScheduled",
          "location": {
            "@type": "Place",
            "name": selectedExhibit.value.name,
            "address": selectedExhibit.value.location || ""
          },
          "image": [
            selectedExhibit.value.coverImage,
            selectedExhibit.value.logo,
            selectedExhibit.value.mapImage
          ].filter(Boolean),
          "description": selectedExhibit.value.description
        }),
        key: 'schema'
      }
    ]
  }
})

async function initPage() {

  pageLoading.value = true
  selectedExhibit.value = {name: "Loading..."}
  hallsData.value = []
  currentPage.value = 1
  totalPages.value = 1

  await fetchExhibitDetails()
  await fetchHalls()
  await trackVisitor()

  await nextTick()

  await setupObserver()
  pageLoading.value = false
}

onMounted(() => {
  if (route.params.slug) {
    initPage()
  }
})

watch(
    () => route.params.slug,
    initPage
)
watch(locale, async (newLocale) => {

    if (!selectedExhibit.value?.slug) return;

    const localizedSlug = selectedExhibit.value.slug[newLocale];
      if (!localizedSlug) {
        await router.push({ name: 'NotFound' });
        return;
      }

    const newPath = `/${newLocale}/exhibit/${localizedSlug}`;

    if (route.fullPath !== newPath) {
        await router.replace(newPath);
    }
    await initPage()
});


onUnmounted(() => {
  observer.value?.disconnect()
  abortController.value?.abort()
})

</script>

<style scoped>
.skeleton-card {
  height: 180px;
  border-radius: 18px;
  background: linear-gradient(
      90deg,
      #f2f2f2 25%,
      #eaeaea 50%,
      #f2f2f2 75%
  );
  background-size: 400% 100%;
  animation: shimmer 1.4s infinite linear;
}

@keyframes shimmer {
  0% {background-position: 100% 0}
  100% {background-position: -100% 0}
}
</style>